import { inject, Injectable } from '@angular/core';
import { API_URL } from '@frontend/basic';
import { Observable, of } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { PaymentWrapper } from '@frontend/masterdata';
import { ConnectionService } from './connection.service';

@Injectable({
  providedIn: 'root',
})
export class PaymentService {
  apiUrl = inject(API_URL);
  http = inject(ConnectionService);

  get origin(): string {
    let origin = window.location.href;
    if (origin.indexOf('?') !== -1) {
      const splUrl = origin.split('?');
      origin = splUrl[0];
    }
    return origin;
  }

  getUrl(channelId: string, paymentId: string): string {
    return `${this.apiUrl}/v1/publicpay/${channelId}/payment/${paymentId}`;
  }

  getPayment(channelId: string, paymentId: string): Observable<PaymentWrapper> {
    return this.http
      .get<{ data: PaymentWrapper, success: boolean }>(this.getUrl(channelId, paymentId))
      .pipe(
        map((val) => val === undefined || val.success === false ? {} as PaymentWrapper : val.data)
      );
  }

  capture(channelId: string, paymentId: string, keys: any): Observable<{ success: boolean }> {
    return this.http.post<{ success: boolean }>(
      `${this.getUrl(channelId, paymentId)}/capture`,
      keys
    ).pipe(
      switchMap((val) => of(val ?? {success: false}))
    );
  }

  setAmazonAuthorizationWithCapture(channelId: string, paymentId: string, keys: any): Observable<{ success: boolean, data: string | undefined }> {
    return this.http.post<{ success: boolean, data: string | undefined }>(
      `${this.getUrl(channelId, paymentId)}/setAmazonAuthorizationWithCapture`,
      keys
    ).pipe(
      switchMap((val) => of(val ?? {success: false, data: undefined}))
    );
  }

  getAmazonCheckout(channelId: string, paymentId: string, keys: any): Observable<{ success: boolean, data: string | undefined }> {
    return this.http.post<{ success: boolean, data: string | undefined }>(
      `${this.getUrl(channelId, paymentId)}/getAmazonCheckout`,
      keys
    ).pipe(
      switchMap((val) => of(val ?? {success: false, data: undefined}))
    );
  }

  getCreateOrderForPaypal(channelId: string, paymentId: string): Observable<{success: boolean, data: string | undefined}> 
  {
    return this.http.post<{ success: boolean, data: string | undefined }>(
      `${this.getUrl(channelId, paymentId)}/createOrderForPaypal`,
      undefined
    ).pipe(
      switchMap((val) => of(val ?? {success: false, data: undefined}))
    );
  }

  chooseProvider(channelId: string, paymentId: string, paymentproviderId: string) {
    return this.http.post<{ success: boolean, data: any }>(
      `${this.getUrl(channelId,paymentId)}/chooseprovider/${paymentproviderId}`,
      {
        origin: this.origin,
      }
    ).pipe(
      switchMap((val) => of(val ?? {data: undefined}))
    );
  }

  getCouponUrl(channelId: string, paymentId: string, couponCode: string): string {
    return `${this.getUrl(channelId, paymentId)}/coupon/${encodeURIComponent(couponCode)}`;
  }

  couponInfo(channelId: string, paymentId: string, couponCode: string): Observable<{ amount: number, error: number, openamount: number }> {
    return this.http.get<{ success: boolean, data: { amount: number, error: number, openamount: number } }>(
      `${this.getCouponUrl(channelId, paymentId, couponCode)}/info`
    ).pipe(
      map(
        (val) => val ? val.data : {amount: 0, error: 1, openamount: 0}
      )
    );
  }

  couponCashing(channelId: string, paymentId: string, couponCode: string): Observable<{ error: number }> {
    return this.http.get<{ success: boolean, data: { error: number } }>(
      `${this.getCouponUrl(channelId, paymentId, couponCode)}/cashing`
    )
    .pipe(
      map((val) => val ? val.data : {error: 1})
    );
  }

  checkSuccess(channelId: string, paymentId: string, seccode: string): Observable<{ success: boolean }> {
    return this.http.post<{ success: boolean }>(
      `${this.getUrl(channelId, paymentId)}/checksuccess`,
      { seccode }
    ).pipe(
      switchMap(val => of(val ?? {success: false}))
    );
  }
}
